import moment from 'moment';

// Determine cell background color based on job type
export const getModelHighlightClass = (job) => {
  if (!job || !job.JobType) return '';
  
  const jobType = job.JobType.toLowerCase();
  
  if (jobType.includes('large station')) return 'highlight-large-station';
  if (jobType.includes('sky harvester')) return 'highlight-sky-harvester';
  if (jobType.includes('retrofit kit')) return 'highlight-retrofit-kit';
  if (jobType.includes('watermax 3000')) return 'highlight-watermax-3000';
  if (jobType.includes('watermax 5000')) return 'highlight-watermax-5000';
  if (jobType.includes('watermax 7000')) return 'highlight-watermax-7000';
  if (jobType.includes('watermax 9000')) return 'highlight-watermax-9000';
  
  return '';
};

// Determine cell background color based on model number
export const getModelNumberHighlightClass = (modelNumber) => {
  if (!modelNumber) return '';
  
  const modelUpperCase = modelNumber.toUpperCase();
  const isShipModel = modelUpperCase.startsWith('SHIP');
  
  if (modelUpperCase.startsWith('HC') || modelUpperCase.startsWith('VTV')) return 'highlight-large-station';
  if (modelUpperCase.startsWith('RET')) return 'highlight-retrofit-kit';
  if (modelUpperCase.startsWith('WM')) {
    if (modelUpperCase.includes('3000')) return 'highlight-watermax-3000';
    if (modelUpperCase.includes('5000')) return 'highlight-watermax-5000';
    if (modelUpperCase.includes('7000')) return 'highlight-watermax-7000';
    if (modelUpperCase.includes('9000')) return 'highlight-watermax-9000';
  }
  if (modelUpperCase.startsWith('SKY') || (modelUpperCase.startsWith('SH') && !isShipModel)) return 'highlight-sky-harvester';
  
  return '';
};

// Determine if job is high priority
export const isHighPriority = (job) => {
  return job && job.JobType && job.JobType.toLowerCase().includes('high priority');
};

// Determine if a completion date is before or after the due date
export const getCompletionStatus = (doneDate, dueDate) => {
  if (!doneDate || !dueDate) return '';
  
  // Convert to Date objects
  const done = new Date(doneDate);
  const due = new Date(dueDate);
  
  // Check if valid dates
  if (isNaN(done.getTime()) || isNaN(due.getTime())) return '';
  
  // Return status based on comparison
  return done <= due ? 'on-time' : 'late';
};

// Determine if a date is outside the current year
export const isOutsideCurrentYear = (dateStr) => {
  if (!dateStr) return false;
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return false;
    const currentYear = new Date().getFullYear();
    return date.getFullYear() !== currentYear;
  } catch (error) {
    console.error('Error checking year of date:', error);
    return false;
  }
};

// Check if a date falls on the start or end date of any week in weekData
export const isWeekBoundaryDate = (dateStr, weekData) => {
  if (!dateStr || !weekData || !weekData.length) return false;
  
  // Parse the date using the local timezone to prevent timezone issues
  let date;
  try {
    const parts = dateStr.split(/[- :]/).map(part => parseInt(part, 10));
    
    // Create date using year, month (0-based), day
    // This ensures we're working with the date as displayed to the user
    if (parts.length >= 3) {
      // If we have year, month, day format
      date = moment([parts[0], parts[1]-1, parts[2]]); // Month is 0-based
    } else {
      // Fallback to standard parsing
      date = moment(dateStr).startOf('day');
    }
  } catch (error) {
    console.error('Error parsing date for weekend check:', error);
    return false;
  }
  
  // Check if the date is a Saturday (day 6) or Sunday (day 0)
  const dayOfWeek = date.day();
  if (dayOfWeek === 0) return 'start'; // Sunday
  if (dayOfWeek === 6) return 'end';   // Saturday
  
  return false;
};

// Check if a job has a TBD (requires tbdList from Redux)
export const hasTbd = (jobId, tbdList) => {
  return tbdList.some(tbdItem => String(tbdItem.ProjectID) === String(jobId));
};

// Check if a job has a move request
export const hasMoveRequest = (job) => {
  return job && job.MoveRequestDateInitiated && !job.MoveRequestDateMovedUpOut;
};

// Check if a job has been moved up
export const hasBeenMovedUp = (job) => {
  return job && job.MoveRequestDateMovedUpOut;
};

// Extract station type from job based on JobType or ModelNumber
export const getStationType = (job) => {
  if (!job) return 'other';

  let modelUpperCase = '';
  let normalizedModel = '';
  let hasPulseCustom = false;
  let hasPulseStock = false;

  if (job.ModelNumber) {
    modelUpperCase = job.ModelNumber.toUpperCase();
    normalizedModel = modelUpperCase.replace(/[\u2010-\u2015]/g, '-').replace(/\s+/g, ' ');
    hasPulseCustom = /3000PC(?:\s*-\s*|\b)/.test(normalizedModel);
    hasPulseStock = /3000P(?:\s*-\s*|\b)/.test(normalizedModel);

    if (hasPulseCustom) return 'pulse-custom';
    if (hasPulseStock && !hasPulseCustom) return 'pulse-stock';
  }
  
  // Check JobType first
  if (job.JobType) {
    const jobType = job.JobType.toLowerCase();
    if (jobType.includes('large station')) return 'large-station';
    if (jobType.includes('retrofit kit')) return 'retrofit-kit';
    if (jobType.includes('watermax 3000')) return 'watermax-3000';
    if (jobType.includes('watermax 5000')) return 'watermax-5000';
    if (jobType.includes('watermax 7000')) return 'watermax-7000';
    if (jobType.includes('watermax 9000')) return 'watermax-9000';
  }
  
  // Check ModelNumber for station-specific classification
  if (job.ModelNumber) {
    if (modelUpperCase.startsWith('HC') || modelUpperCase.startsWith('VTV')) return 'large-station';
    if (modelUpperCase.startsWith('RET')) return 'retrofit-kit';
    if (modelUpperCase.startsWith('SHM')) return 'sky-harvester-micro';
    if (modelUpperCase.startsWith('SH80')) return 'sky-harvester-sh80';
    if (modelUpperCase.startsWith('SH') && !modelUpperCase.startsWith('SHIP')) return 'sky-harvester-custom';
    if (modelUpperCase.startsWith('WM')) {
      if (modelUpperCase.includes('3000')) return 'watermax-3000';
      if (modelUpperCase.includes('5000')) return 'watermax-5000';
      if (modelUpperCase.includes('7000')) return 'watermax-7000';
      if (modelUpperCase.includes('9000')) return 'watermax-9000';
    }
    if (modelUpperCase.startsWith('SKY')) return 'sky-harvester-custom';
  }

  // Fallback for jobs identified as Sky Harvester without a matching SH model prefix
  if (job.JobType && job.JobType.toLowerCase().includes('sky harvester')) {
    return 'sky-harvester-custom';
  }
  
  return 'other';
};
